<template>
  <div class="d-flex align-center mt-5 mb-7">
    <v-icon
      class="laptop-icon mr-3"
      :class="{ offline: deviceDetails.offline }"
      :icon="deviceIcon"
      size="40"
      data-testid="devices-preview-device-icon"
    >
    </v-icon>
    <span
      :class="{ 'text-indigo-medium': deviceDetails.offline }"
      class="headline5"
      data-testid="devices-preview-device-header"
    >
      {{ isMobile ? deviceDetails.device.model : deviceDetails.device.hostname }}
      <span v-if="deviceDetails.offline" data-testid="devices-preview-is-offline">{{
        ` (${$t("general.offline")})`
      }}</span>
    </span>
  </div>
  <div
    v-if="deviceDetails.mergeState === DuplicateDeviceMergeState.PENDING_POTENTIAL_MERGE"
    class="warning-block pa-4"
    data-testid="devices-preview-duplicate-warning"
  >
    <div class="d-flex justify-space-between align-start">
      <div class="d-flex align-start flex-grow-1">
        <v-icon size="24" icon="$warning" />
        <div class="d-flex flex-column flex-grow-1 ml-2">
          <div class="subtitle1 mb-2">
            {{ $t("deviceDetails.duplicateBanner.title") }}
          </div>
          <div class="body2 mb-2" v-html="$t('deviceDetails.duplicateBanner.description')"></div>
          <div class="d-flex">
            <span
              class="text-decoration-underline body2"
              role="button"
              data-testid="devices-preview-ignore-duplicates-btn"
              @click="$emit('ignore-duplicates-clicked')"
            >
              {{ $t("deviceDetails.duplicateBanner.ignore") }}
            </span>
            <v-divider class="mx-3" vertical />
            <span
              class="text-decoration-underline body2"
              role="button"
              data-testid="devices-preview-merge-duplicates-btn"
              @click="$emit('merge-duplicates-clicked')"
            >
              {{ $t("deviceDetails.duplicateBanner.merge") }}
            </span>
          </div>
        </div>
      </div>
      <v-icon
        size="24"
        role="button"
        icon="$x"
        data-testid="devices-preview-close-duplicate-warning-btn"
        @click="deviceDetails.mergeState = DuplicateDeviceMergeState.MERGE_IGNORED"
      />
    </div>
  </div>

  <v-row class="mb-2">
    <v-col cols="6">
      <div v-if="isMspContext" class="mb-4" data-testid="devices-preview-workspace-section">
        <div class="subtitle1" data-testid="devices-preview-workspace-header">
          {{ $t("general.workspace") }}
        </div>
        <div v-if="workspaceId" class="body1" data-testid="devices-preview-workspace-id">
          {{ workspaceId }}
        </div>
        <div
          v-if="workspaceName"
          role="button"
          tabindex="0"
          class="coro-link"
          data-testid="devices-preview-workspace-name"
          @click="goToWorkspace"
          @keydown.enter="goToWorkspace"
        >
          {{ workspaceName }}
        </div>
      </div>
      <div class="subtitle1" data-testid="devices-preview-model-id-header">
        {{ $t("deviceDetails.device.modelAndId") }}
      </div>
      <div v-if="deviceDetails.device.model" class="body1" data-testid="devices-preview-model">
        {{ $t("deviceDetails.device.model") }}:
        {{ deviceDetails.device.model }}
      </div>
      <div v-if="deviceDetails.deviceModelId" class="body1" data-testid="devices-preview-model-id">
        {{ $t("deviceDetails.device.modelId") }}:
        {{ deviceDetails.deviceModelId }}
      </div>
      <div v-if="isMobile && deviceDetails.device.id" class="body1" data-testid="devices-device-id">
        {{ $t("deviceDetails.device.mobileId") }}:
        {{ deviceDetails.device.id }}
      </div>
      <div
        v-else-if="deviceDetails.deviceSerialNumber"
        class="body1"
        data-testid="devices-preview-serial-number"
      >
        {{ $t("deviceDetails.device.serialNumber") }}:
        {{ deviceDetails.deviceSerialNumber }}
      </div>
      <div
        v-if="deviceDetails.enrollmentCode"
        class="body1 mb-4"
        data-testid="devices-preview-enrollment-code"
      >
        {{ $t("deviceDetails.device.deviceID") }}:
        {{ deviceDetails.enrollmentCode }}
      </div>
      <div class="subtitle1" data-testid="devices-preview-os-header">
        {{ $t("deviceDetails.device.os") }}
      </div>
      <div class="body1 mb-4" data-testid="devices-preview-os">
        {{ deviceDetails.osDisplayName }}
      </div>
      <template v-if="isMobile">
        <div class="subtitle1" data-testid="devices-preview-username-header">
          {{ $t("deviceDetails.device.username") }}
        </div>
        <div class="body1" data-testid="devices-preview-username">
          {{ deviceDetails.lastLoggedInUsers[0] ?? deviceDetails.email }}
        </div>
      </template>
      <template v-else>
        <div class="subtitle1" data-testid="devices-preview-hostname-header">
          {{ $t("deviceDetails.device.hostName") }}
        </div>
        <div class="body1" data-testid="devices-preview-hostname">
          {{ deviceDetails.device.hostname }}
        </div>
      </template>
      <template v-if="deviceDetails.lastKnownIp">
        <div class="subtitle1 mt-4" data-testid="devices-preview-last-known-ip-header">
          {{ $t("deviceDetails.device.lastKnownIp") }}
        </div>
        <div class="body1" data-testid="devices-preview-last-known-ip">
          {{ deviceDetails.lastKnownIp }}
        </div>
      </template>
      <template v-if="shouldShowVerifiedEmail">
        <div class="subtitle1 mt-4">
          {{ $t("deviceDetails.device.verifiedEmail") }}
        </div>
        <div class="body1" data-testid="devices-preview-model">
          {{ deviceDetails.verifiedEmail }}
        </div>
      </template>
      <template v-if="deviceDetails.networkStatus && deviceDetails.osType !== OsType.IOS">
        <div class="subtitle1 mt-4">
          {{ $t("deviceDetails.device.networkStatus") }}
        </div>
        <div class="body1" data-testid="devices-preview-network-status">
          {{ $t(`deviceDetails.device.networkStatusValues.${deviceDetails.networkStatus}`) }}
        </div>
      </template>
    </v-col>
    <v-col cols="6">
      <div class="subtitle1 mb-1" data-testid="devices-preview-coro-protection-header">
        {{ $t("deviceDetails.device.coroProtection") }}
      </div>
      <div
        class="body1"
        :class="{ 'text-red-dark': deviceDetails.updateStatus === AgentUpdateStatus.OUTDATED }"
      >
        {{ $t("deviceDetails.device.version") }}
        <span data-testid="devices-preview-coro-version">{{ deviceDetails.appVersion }}</span>
        <span
          v-if="deviceDetails.marketingVersion"
          data-testid="devices-preview-coro-marketing-version"
        >
          ({{ deviceDetails.marketingVersion }})</span
        >
        <div
          v-if="deviceDetails.updateStatus"
          class="d-flex align-center body1"
          :class="getUpdateStatusClass(deviceDetails.updateStatus)"
          data-testid="devices-preview-agent-update-status"
        >
          <span>
            {{ $t(`devices.statuses.${deviceDetails.updateStatus}`) }}
          </span>
          <v-tooltip
            v-if="deviceDetails.updateStatus === AgentUpdateStatus.UPDATE_PENDING"
            open-delay="300"
            location="top"
          >
            <template #activator="{ props }">
              <v-icon v-bind="props" class="ml-1" icon="icon-info-solid-xs" size="16" />
            </template>
            {{ $t(`devices.tooltips.${deviceDetails.updateStatus}`) }}
          </v-tooltip>
        </div>
        <div
          v-if="deviceDetails.rebootRequired"
          class="body1 text-red-dark"
          data-testid="devices-preview-reboot-required"
        >
          {{ $t("deviceDetails.device.rebootNeeded") }}
        </div>
        <div
          v-if="deviceDetails.notSupported"
          class="body1 text-red-dark"
          data-testid="devices-preview-linux-not-supported-notice"
        >
          {{ $t("deviceDetails.device.linuxNotSupported") }}
        </div>
      </div>
      <div class="body1 mb-2">
        {{ $t("deviceDetails.device.added") }}
        <span data-testid="devices-preview-coro-added-date">
          {{ getFormattedDateTime(deviceDetails.activationTime, "ll") }}
        </span>
      </div>
      <device-scan-section
        :device-details="deviceDetails"
        @scroll-to-activity-logs="$emit('scroll-to-activity-logs')"
      />
      <div
        v-if="showLogsUploadedDate"
        class="body2 text--grey"
        data-testid="devices-preview-logs-uploaded-date"
      >
        {{ $t("deviceDetails.device.lastLogsUploadedAt") }}
        {{ getFormattedDateTime(deviceDetails.lastLogsUploadedAt) }}
      </div>
      <template v-if="deviceDetails.lastActivity > 0">
        <div class="subtitle1 mt-4" data-testid="devices-preview-last-seen-header">
          {{ $t("deviceDetails.lastSeen") }}
        </div>
        <div class="body1" data-testid="devices-preview-last-seen">
          {{ getFormattedDateTime(deviceDetails.lastActivity) }}
        </div>
      </template>
      <template v-if="!!deviceDetails.vpnIp">
        <div class="subtitle1 mt-4" data-testid="devices-preview-static-ip-header">
          {{ $t("deviceDetails.device.staticIp") }}
        </div>
        <div class="body1" data-testid="devices-preview-static-ip">
          {{ deviceDetails.vpnIp }}
        </div>
      </template>
      <template v-if="deviceDetails.labels?.length">
        <div class="subtitle1 mt-4 mb-3" data-testid="devices-preview-labels-header">
          {{ $t("deviceDetails.labels") }}
        </div>
        <div data-testid="devices-preview-labels">
          <template v-for="label in deviceDetails.labels" :key="label.id">
            <v-chip
              variant="flat"
              label
              v-if="!label.predefined"
              :color="label.color"
              size="small"
              class="mr-1 mb-1"
              data-testid="devices-preview-label-chip"
            >
              <div class="d-flex align-center justify-space-between">
                <span class="text-uppercase mr-1" data-testid="devices-preview-label-name">
                  {{ label.name.replace(/ /g, "&nbsp;") }}
                </span>
                <v-icon
                  v-if="!actionNotAllowed"
                  size="16"
                  @click="$emit('action-clicked', DeviceAction.REMOVE_LABEL, deviceDetails, label)"
                  icon="$closeCircle"
                  data-testid="devices-preview-remove-label-btn"
                />
              </div>
            </v-chip>
          </template>
        </div>
      </template>
    </v-col>
  </v-row>
</template>
<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import type { DeviceDetails } from "@/_store/devices.module.ts";
import type { MspDeviceDetails } from "@/_store/msp/devices/msp-devices.module.ts";
import {
  AgentUpdateStatus,
  DeviceAction,
  DuplicateDeviceMergeState,
  OsType,
} from "@/constants/devices.ts";
import DeviceScanSectionComponent from "@/components/devices/device-preview/DeviceScanSection.vue";
import { getFormattedDateTime } from "@/_helpers/date";
import { usePermissionsStore } from "@/_store/permissions.module.ts";
import { AppContext } from "@/types.ts";
import { useRouter } from "vue-router";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useAccountStore } from "@/_store/account.module.ts";
import { RouteName } from "@/constants/routes.ts";

type DevicePreviewDetails = DeviceDetails &
  Partial<Pick<MspDeviceDetails, "workspaceId" | "workspaceName">>;

export default defineComponent({
  components: {
    DeviceScanSection: DeviceScanSectionComponent,
  },
  props: {
    actionNotAllowed: {
      type: Boolean,
      required: true,
    },
    deviceDetails: {
      type: Object as PropType<DevicePreviewDetails>,
      required: true,
    },
    isMobile: {
      type: Boolean,
      required: true,
    },
    context: {
      type: String as PropType<AppContext>,
      default: AppContext.SINGLE_WORKSPACE,
    },
  },
  emits: [
    "action-clicked",
    "merge-duplicates-clicked",
    "ignore-duplicates-clicked",
    "scroll-to-activity-logs",
  ],
  setup(props) {
    const permissionsStore = usePermissionsStore();
    const workspaceStore = useWorkspaceStore();
    const router = useRouter();
    const accountStore = useAccountStore();

    const showLogsUploadedDate = computed(() => {
      return permissionsStore.isGlobalAdmin && props.deviceDetails.logsUploaded;
    });
    const deviceIcon = computed(() => {
      if (props.deviceDetails.osType) {
        const iconMap = {
          [OsType.WINDOWS]: `$windowsPc`,
          [OsType.OSX]: `$macPc`,
          [OsType.IOS]: `$iosDevice`,
          [OsType.ANDROID]: `$androidDevice`,
          [OsType.MAC_OS]: `$macPc`,
          [OsType.LINUX]: `$linuxPc`,
        };
        return iconMap[props.deviceDetails.osType];
      }
      return undefined;
    });

    const isMspContext = computed(() => props.context === AppContext.MSP);
    const workspaceId = computed(() => (isMspContext.value ? props.deviceDetails.workspaceId : ""));
    const workspaceName = computed(() =>
      isMspContext.value ? props.deviceDetails.workspaceName : ""
    );

    const shouldShowVerifiedEmail = computed(() => {
      return !isMspContext.value && Boolean(props.deviceDetails.verifiedEmail);
    });

    const goToWorkspace = async () => {
      if (!isMspContext.value || !workspaceId.value) return;
      await workspaceStore.updateWorkspaceAndPermissions(workspaceId.value, false);
      await accountStore.checkMspAccess();
      await router.push({ name: RouteName.DASHBOARD_PAGE });
    };

    const getUpdateStatusClass = (status?: AgentUpdateStatus): string => {
      switch (status) {
        case AgentUpdateStatus.OUTDATED:
          return "text-red-dark";
        case AgentUpdateStatus.UPDATE_AVAILABLE:
          return "text-yellow-base";
        case AgentUpdateStatus.UPDATE_PENDING:
          return "text-indigo-base";
        default:
          return "";
      }
    };

    return {
      AgentUpdateStatus,
      DeviceAction,
      getUpdateStatusClass,
      deviceIcon,
      showLogsUploadedDate,
      getFormattedDateTime,
      DuplicateDeviceMergeState,
      AppContext,
      isMspContext,
      workspaceId,
      workspaceName,
      goToWorkspace,
      shouldShowVerifiedEmail,
      OsType,
    };
  },
});
</script>
<style lang="scss" scoped>
.laptop-icon {
  &.offline:before {
    color: rgb(var(--v-theme-indigo-medium));
  }
}
:deep(*) {
  .label-item {
    display: inline-block;
    border-radius: 2px;
    overflow-wrap: initial;
    padding: 0 4px;

    span {
      max-width: 246px;
      overflow-x: clip;
      display: inline-block;
      text-overflow: ellipsis;
    }
  }
}
</style>
